import * as THREE from 'three/webgpu';
import { TERRAIN, buildCheckpoints, startPose } from './terrain/heightfield.js';
import { createTerrainMesh } from './terrain/mesh.js';
import { setupLighting, createWater, buildScenery } from './world/environment.js';
import { Jeep } from './vehicle/jeep.js';
import { createJeepModel, updateJeepVisuals } from './vehicle/model.js';
import { Skidmarks } from './world/skidmarks.js';
import { DustParticles } from './world/particles.js';
import { RaceDirector } from './core/race.js';
import { CameraRig } from './core/camera.js';
import { InputController } from './core/input.js';
import { HUD } from './core/hud.js';

async function bootstrap() {
  const bootStatus = document.getElementById('boot-status');
  const bootScreen = document.getElementById('boot');
  const errBox = document.getElementById('err-box');

  const showError = (err) => {
    console.error('Fatal initialization error:', err);
    if (errBox) {
      errBox.style.display = 'block';
      errBox.textContent = `Ошибка инициализации рендера:\n${err.message || err}`;
    }
    if (bootStatus) {
      bootStatus.textContent = 'Ошибка инициализации графического ускорителя';
      bootStatus.style.color = '#ef4444';
    }
  };

  try {
    if (bootStatus) bootStatus.textContent = 'Инициализация Three.js WebGPU…';

    // 1. Renderer Initialization
    const renderer = new THREE.WebGPURenderer({
      antialias: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.domElement.id = 'webgpu-canvas';
    document.body.appendChild(renderer.domElement);

    // Initialize backend (WebGPU or WebGL2 fallback)
    await renderer.init();
    const isWebGPU = renderer.backend?.isWebGPUBackend === true;

    // 2. Scene & Camera
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xa5cbe8);
    scene.fog = new THREE.FogExp2(0xb8d9f2, 0.0016);

    const camera = new THREE.PerspectiveCamera(
      62,
      window.innerWidth / window.innerHeight,
      0.2,
      1400
    );

    if (bootStatus) bootStatus.textContent = 'Генерация процедурного рельефа и окружения…';

    // 3. Lighting, Atmosphere & Water
    setupLighting(scene);
    createWater(scene);

    // 4. Terrain & Track
    const terrain = createTerrainMesh();
    scene.add(terrain);

    // 5. Scenery & Checkpoints
    const checkpoints = buildCheckpoints(12);
    const scenery = buildScenery(scene, checkpoints);

    // 6. Jeep & Effects
    if (bootStatus) bootStatus.textContent = 'Сборка физической модели джипа…';
    const jeep = new Jeep();
    const sp = startPose();
    jeep.reset(sp.x, sp.z, sp.yaw);

    const model = createJeepModel();
    scene.add(model.root);

    const skidmarks = new Skidmarks(scene);
    const particles = new DustParticles(scene);

    // 7. Core systems: Race, Camera, Input, HUD
    const race = new RaceDirector(checkpoints);
    race.start();

    const camRig = new CameraRig(camera);
    const hud = new HUD(checkpoints, TERRAIN.loop);
    hud.setBackend(isWebGPU ? 'WebGPU' : 'WebGL 2');
    hud.setCameraMode(camRig.mode);

    const input = new InputController();

    input.onCameraToggle = () => {
      const mode = camRig.toggleMode();
      hud.setCameraMode(mode);
    };

    input.onRespawn = () => {
      const rp = race.respawnPose();
      jeep.reset(rp.x, rp.z, rp.yaw);
      hud.showNotification('РЕСПАУН НА ТРАССУ');
    };

    // 8. Precompilation to prevent first-frame jank
    if (bootStatus) bootStatus.textContent = 'Компиляция шейдеров и конвейера…';
    await renderer.compileAsync(scene, camera);

    // Remove boot overlay
    if (bootScreen) {
      bootScreen.classList.add('gone');
    }
    hud.showNotification('ГОНКА НАЧАЛАСЬ!', 'УПРАВЛЕНИЕ: W / A / S / D + SPACE (ДРИФТ) + SHIFT (БУСТ)');

    // Expose runtime handle for diagnostics and automated testing
    window.__game = {
      renderer,
      scene,
      camera,
      jeep,
      race,
      hud,
      camRig,
      input,
      frameCount: 0
    };

    // 9. Resize Handling
    window.addEventListener('resize', () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    });

    // 10. Main Animation Loop
    let lastTime = performance.now();
    let ringAnimTime = 0;

    renderer.setAnimationLoop(() => {
      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.08);
      lastTime = now;
      if (window.__game) window.__game.frameCount++;

      // Physics integration
      jeep.step(dt, input.state);

      // Visual updates
      updateJeepVisuals(model, jeep, input.state);
      skidmarks.update(dt, jeep, input.state);
      particles.update(dt, jeep, input.state);

      // Race logic
      race.update(dt, jeep, (title, sub) => {
        hud.showNotification(title, sub);
      });

      // Checkpoint rings visual pulsing
      ringAnimTime += dt;
      const activeIdx = race.activeCpIndex;
      for (let i = 0; i < scenery.gateRings.length; i++) {
        const ring = scenery.gateRings[i];
        if (i === activeIdx) {
          ring.visible = true;
          ring.material.color.setHex(0xf59e0b);
          const pulse = 1.0 + Math.sin(ringAnimTime * 6.0) * 0.08;
          ring.scale.set(pulse, pulse, pulse);
        } else if (i < activeIdx) {
          ring.visible = true;
          ring.material.color.setHex(0x06b6d4);
          ring.scale.set(1, 1, 1);
        } else {
          ring.visible = true;
          ring.material.color.setHex(0x64748b);
          ring.scale.set(1, 1, 1);
        }
      }

      // Camera & HUD
      camRig.update(dt, jeep);
      hud.update(jeep, race);

      // Render frame
      renderer.render(scene, camera);
    });

  } catch (err) {
    showError(err);
  }
}

window.addEventListener('DOMContentLoaded', bootstrap);
